// utils/auth.ts

// Enhanced JWT token retrieval that handles multiple cookie sources
export const getJwtFromCookie = () => {
  // Try multiple cookie sources for cross-domain compatibility
  const cookieSources = ['jwt', 'auth_token', 'id_token'];
  
  for (const cookieName of cookieSources) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${cookieName}=([^;]*)`));
    if (match) {
      return decodeURIComponent(match[1]);
    }
  }
  
  return null;
};

// Get user info from cookie
export const getUserInfoFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )user_info=([^;]*)/);
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (error) {
      console.error('Failed to parse user_info cookie:', error);
      return null;
    }
  }
  return null;
};

// Validate Kinde JWT format and expiration
export const validateKindeJwt = (token: string): boolean => {
  try {
    // Check if it's a valid JWT format (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token is expired
    const currentTime = Date.now() / 1000;
    if (payload.exp && payload.exp < currentTime) {
      return false;
    }
    
    // Check if token has required fields
    if (!payload.sub || !payload.email) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating JWT:', error);
    return false;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const jwt = getJwtFromCookie();
  if (!jwt) return false;
  
  return validateKindeJwt(jwt);
};

// Get user ID from JWT
export const getUserIdFromJwt = () => {
  const jwt = getJwtFromCookie();
  if (!jwt) return null;
  
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    // Kinde JWT uses 'sub' field for user ID
    return payload.sub || null;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

// Get user email from JWT
export const getUserEmailFromJwt = () => {
  const jwt = getJwtFromCookie();
  if (!jwt) return null;
  
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    // Kinde JWT uses 'email' field for user email
    return payload.email || null;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

// Clear all auth cookies
export const clearAuthCookies = () => {
  const cookies = ['jwt', 'auth_token', 'id_token', 'user_info', 'ac-state-key', 'authenticated'];
  const domain = window.location.hostname === 'localhost' ? undefined : '.streamio.ai';
  const domainOption = domain ? `domain=${domain};` : '';
  const secureOption = window.location.hostname === 'localhost' ? '' : 'secure;';
  const sameSiteOption = window.location.hostname === 'localhost' ? 'samesite=lax' : 'samesite=none';
  
  cookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; path=/; ${domainOption} max-age=0; ${secureOption} ${sameSiteOption}`;
  });
};

// Redirect to landing page for authentication
export const redirectToAuth = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Use environment variable if set, otherwise use environment-appropriate fallback
  let landingUrl = process.env.REACT_APP_LANDING_URL;
  
  if (!landingUrl) {
    if (isLocal || isDevelopment) {
      landingUrl = 'http://localhost:3000';
    } else {
      landingUrl = 'https://www.streamio.ai';
    }
  }
  
  // Log for debugging
  console.log('Redirecting to auth:', {
    landingUrl,
    isLocal,
    isDevelopment,
    hostname: window.location.hostname,
    nodeEnv: process.env.NODE_ENV
  });
  
  window.location.href = `${landingUrl}/api/auth/login`;
}; 